import type { TabId } from '../pages/summary/Tabs';
import {
	getCurrentScreenLayout,
	getScreenLayout,
	isScreenLayoutEmbedded,
	isScreenLayoutEmbeddedOrOverlay,
	isScreenLayoutFullScreen,
	type NavinaScreenLayout,
} from '../utilities/screenLayout';
import {
	getOrCreateAddonCommunicator,
	type OpenSummaryProtocolTypes,
	type OverlayProtocolTypes,
} from './addon/addon-communicator';
import { type ReactNode, createContext, useContext, useMemo, useState } from 'react';

export interface NavinaData {
	readonly isOverlayOrEmbedded: boolean;
	readonly isEmbedded: boolean;
	readonly isFullScreen: boolean;
	readonly screenLayout: NavinaScreenLayout;
	readonly pathname: string;
	isQualityTool: boolean; // TODO - make readonly
	readonly isPostVisit: boolean;
	readonly setPostVisit: (value: boolean) => void;
	readonly activeTab: TabId | undefined;
	readonly setActiveTab: (newActiveTab: TabId) => void;
	readonly sendMessageToAddon: (eventType: OverlayProtocolTypes | OpenSummaryProtocolTypes, params?: any) => void;
}

const initialScreenLayout = getCurrentScreenLayout();

const NavinaContext = createContext<NavinaData>({
	isOverlayOrEmbedded: isScreenLayoutEmbeddedOrOverlay(initialScreenLayout),
	isEmbedded: isScreenLayoutEmbedded(initialScreenLayout), // Shall be deprecated, replaced by screenLayout
	isFullScreen: isScreenLayoutFullScreen(initialScreenLayout), // Shall be deprecated, replaced by screenLayout
	screenLayout: initialScreenLayout,
	sendMessageToAddon,
	pathname: globalThis.location.pathname,
	activeTab: undefined,
	setActiveTab: function noop(): void {},
	isPostVisit: false,
	setPostVisit: function noop(): void {},
	isQualityTool: false,
});

export function useNavinaData(): NavinaData {
	return useContext(NavinaContext);
}

export function NavinaDataProvider({ children }: Readonly<{ children: ReactNode }>) {
	const [activeTab, setActiveTab] = useState<TabId | undefined>(undefined);

	const { pathname: currentPathname } = globalThis.location;

	const memoizedScreenLayout = useMemo<NavinaScreenLayout>(
		function getUpdatedScreenLayout(): NavinaScreenLayout {
			return getScreenLayout({ locationPathname: currentPathname });
		},
		[currentPathname],
	);

	const contextValue = useMemo<NavinaData>(
		() => ({
			isPostVisit: false,
			setPostVisit: function noop(): void {},
			activeTab,
			setActiveTab,
			pathname: currentPathname,
			isOverlayOrEmbedded: isScreenLayoutEmbeddedOrOverlay(memoizedScreenLayout), // Shall be deprecated, replaced by screenLayout
			isFullScreen: isScreenLayoutFullScreen(memoizedScreenLayout), // Shall be deprecated, replaced by screenLayout
			isEmbedded: isScreenLayoutEmbedded(memoizedScreenLayout), // Shall be deprecated, replaced by screenLayout
			screenLayout: memoizedScreenLayout,
			sendMessageToAddon: sendMessageToAddon,
			isQualityTool: false,
		}),
		[activeTab, memoizedScreenLayout],
	);

	return <NavinaContext.Provider value={contextValue}>{children}</NavinaContext.Provider>;
}

function sendMessageToAddon(eventType: OverlayProtocolTypes | OpenSummaryProtocolTypes, params?: any): void {
	getOrCreateAddonCommunicator().sendToAddon(eventType, params);
}
